import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import {
  createEvent,
  deleteEvent,
  getEvent,
  getEventOccurrences,
  updateEvent,
  type EventOccurrence,
  type EventPatch,
} from '../api.js';
import { useCaptured } from '../capture/CaptureProvider.js';
import {
  addDays,
  addMonths,
  dayKey,
  hhmm,
  layLanes,
  localIso,
  minutesOf,
  parseDay,
  spanDays,
  timeLabel,
  viewDays,
  viewTitle,
  weekStartsOn,
  monthGrid,
  type CalView,
} from '../lib/calendar.js';
import { getUpcomingRange, listProjects } from '../lifeos/api.js';
import { sphereColor, sphereName, useSpheres } from '../lifeos/spheres.js';
import { errorText, useToast } from '../lifeos/toast.js';
import type { Project, Sphere, UpcomingItem } from '../lifeos/types.js';
import { Button, Icon, Page, Segmented, Sheet } from '../ui/index.js';

/**
 * Calendar (owner: "an actual calendar — the month — not just a list").
 * Month grid with Week and Day time grids. Events come from
 * `GET /api/calendar?expand=1&from&to` (recurring rows expanded per
 * occurrence, each carrying its job); tasks, obligations and the rest from
 * `GET /api/upcoming?from&to`. Chips are coloured by job. A day opens a panel
 * (beside the grid on desktop, below it on a phone) with "Add event"; an event
 * opens a sheet to edit or delete it. Plan (/plan) keeps goals and routines.
 */

const MAX_CHIPS = 3;
const HOUR_FROM = 6;
const HOUR_TO = 23;
const HOUR_PX_DEFAULT = 48;
/** Chip row height in the month grid (chip + gap), and the day number's row. */
const CHIP_PX = 22;
const DAYNUM_PX = 34;

/**
 * Height from `el`'s top to the bottom of the window (desktop only), so the
 * month grid and time grid fill the screen instead of stopping halfway.
 * null on phones / narrow windows: those keep their natural height.
 */
function useFillHeight(): [RefObject<HTMLDivElement>, number | null] {
  const ref = useRef<HTMLDivElement>(null);
  const [h, setH] = useState<number | null>(null);
  useLayoutEffect(() => {
    const measure = (): void => {
      const el = ref.current;
      if (!el || window.innerWidth < 900) return setH(null);
      const top = el.getBoundingClientRect().top + window.scrollY;
      setH(Math.max(480, Math.floor(window.innerHeight - top - 24)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return [ref, h];
}

interface CalItem {
  id: string;
  kind: 'event' | 'birthday' | UpcomingItem['type'];
  title: string;
  sphere: string;
  allDay: boolean;
  startsAt: string | null;
  endsAt: string | null;
  days: string[];
  occ?: EventOccurrence;
}

const VIEWS = [
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
  { value: 'day', label: 'Day' },
] as const;

function toItems(occs: EventOccurrence[], upcoming: UpcomingItem[]): CalItem[] {
  const out: CalItem[] = [];
  for (const o of occs) {
    if (o.kind === 'moment') continue; // private notes about other people: People pages only
    out.push({
      id: `ev:${o.eventId}:${o.startsAt}`,
      kind: o.kind === 'birthday' ? 'birthday' : 'event',
      title: o.title,
      sphere: o.sphere ?? 'personal',
      allDay: o.allDay,
      startsAt: o.startsAt,
      endsAt: o.endsAt,
      days: spanDays(o.startsAt, o.endsAt, o.allDay),
      occ: o,
    });
  }
  for (const u of upcoming) {
    if (u.type === 'event' || u.type === 'birthday') continue; // already from the calendar feed
    out.push({
      id: `${u.type}:${u.id}:${u.dueOn}`,
      kind: u.type,
      title: u.title,
      sphere: u.sphere,
      allDay: true,
      startsAt: null,
      endsAt: null,
      days: [u.dueOn],
    });
  }
  return out;
}

function sortItems(a: CalItem, b: CalItem): number {
  if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
  return (a.startsAt ?? '').localeCompare(b.startsAt ?? '') || a.title.localeCompare(b.title);
}

const KIND_LABEL: Record<string, string> = {
  task: 'Action item',
  birthday: 'Birthday',
  obligation: 'Due',
  document: 'Document',
  service: 'Renewal',
  warranty: 'Warranty',
};

export default function Calendar(): JSX.Element {
  const spheres = useSpheres();
  const toast = useToast();
  const firstDay = useMemo(() => weekStartsOn(), []);
  const today = dayKey(new Date());
  const [view, setView] = useState<CalView>('month');
  const [cursor, setCursor] = useState(today);
  const [selected, setSelected] = useState<string | null>(null);
  const [items, setItems] = useState<CalItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [open, setOpen] = useState<EventOccurrence | null>(null);
  const [adding, setAdding] = useState<string | null>(null);

  const days = useMemo(() => viewDays(view, cursor, firstDay), [view, cursor, firstDay]);
  const from = days[0]!;
  const to = days[days.length - 1]!;

  const load = useCallback(async () => {
    try {
      const fromIso = localIso(from, '00:00');
      const toIso = new Date(parseDay(addDays(to, 1)).getTime() - 1).toISOString();
      const [occs, up] = await Promise.all([
        getEventOccurrences(fromIso, toIso),
        getUpcomingRange(from, to).catch(() => [] as UpcomingItem[]),
      ]);
      setItems(toItems(occs, up));
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, [from, to]);

  useEffect(() => {
    void load();
  }, [load]);
  useCaptured(() => void load());

  useEffect(() => {
    listProjects().then(setProjects, () => undefined);
  }, []);

  const byDay = useMemo(() => {
    const m = new Map<string, CalItem[]>();
    for (const it of items ?? []) for (const d of it.days) m.set(d, [...(m.get(d) ?? []), it]);
    for (const list of m.values()) list.sort(sortItems);
    return m;
  }, [items]);

  function step(n: number): void {
    setCursor((c) => (view === 'month' ? addMonths(c, n) : addDays(c, view === 'week' ? 7 * n : n)));
  }

  function pickDay(d: string): void {
    setSelected(d);
    setAdding(null);
    if (view === 'month' && d.slice(0, 7) !== cursor.slice(0, 7)) setCursor(d);
  }

  function openItem(it: CalItem, day: string): void {
    if (it.occ && it.kind === 'event') setOpen(it.occ);
    else pickDay(day);
  }

  const weekdays = useMemo(
    () =>
      monthGrid(today, firstDay)[0]!.map((d) => ({
        short: parseDay(d).toLocaleDateString(undefined, { weekday: 'short' }),
        narrow: parseDay(d).toLocaleDateString(undefined, { weekday: 'narrow' }),
      })),
    [today, firstDay],
  );

  const panelDay = selected ?? (view === 'day' ? cursor : null);
  const [fillRef, fillH] = useFillHeight();
  const weeks = monthGrid(cursor, firstDay);
  // Month: as many chips as each cell has room for once the grid fills the screen.
  const maxChips = fillH ? Math.max(MAX_CHIPS, Math.floor(((fillH - 32) / weeks.length - DAYNUM_PX) / CHIP_PX) - 1) : MAX_CHIPS;

  return (
    <Page
      title="Calendar"
      wide
      className="cal"
      sub={
        <>
          Events, action items and birthdays by day · <Link to="/plan">Plan</Link> has goals and routines
        </>
      }
    >
      <div className="cal__bar">
        <div className="cal__nav">
          <button type="button" className="cal__navbtn" aria-label="Previous" onClick={() => step(-1)}>
            <Icon name="chevron-left" size={20} />
          </button>
          <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => { setCursor(today); setSelected(today); }}>
            Today
          </button>
          <button type="button" className="cal__navbtn" aria-label="Next" onClick={() => step(1)}>
            <Icon name="chevron-right" size={20} />
          </button>
          <h2 className="cal__title" aria-live="polite">{viewTitle(view, cursor, firstDay)}</h2>
        </div>
        <Segmented label="View" value={view} options={VIEWS} onChange={(v) => setView(v)} />
      </div>
      {error && <p className="cal__error" role="alert">Couldn't load the calendar: {error}</p>}

      <div className={`cal__layout${panelDay ? ' cal__layout--panel' : ''}`}>
        <div className="cal__main" ref={fillRef}>
          {view === 'month' ? (
            <div
              className={`cal__month${fillH ? ' cal__month--fill' : ''}`}
              role="grid"
              aria-label={viewTitle('month', cursor, firstDay)}
              style={fillH ? { height: fillH } : undefined}
            >
              <div className="cal__dow" role="row">
                {weekdays.map((w, i) => (
                  <span key={i} role="columnheader" className="cal__dowcell">
                    <span className="cal__dow-long">{w.short}</span>
                    <span className="cal__dow-narrow" aria-hidden="true">{w.narrow}</span>
                  </span>
                ))}
              </div>
              {weeks.map((week) => (
                <div key={week[0]} className="cal__week" role="row">
                  {week.map((d) => {
                    const list = byDay.get(d) ?? [];
                    const shown = Math.min(list.length, maxChips);
                    const extra = list.length - shown;
                    const cls = [
                      'cal__cell',
                      d === today ? 'cal__cell--today' : '',
                      d === selected ? 'cal__cell--on' : '',
                      d.slice(0, 7) !== cursor.slice(0, 7) ? 'cal__cell--out' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    return (
                      <div key={d} role="gridcell" className={cls} data-day={d} onClick={() => pickDay(d)}>
                        <button
                          type="button"
                          className="cal__daynum"
                          aria-label={parseDay(d).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                          aria-current={d === today ? 'date' : undefined}
                          onClick={(e) => {
                            e.stopPropagation();
                            pickDay(d);
                          }}
                        >
                          {parseDay(d).getDate()}
                        </button>
                        <div className="cal__chips">
                          {list.slice(0, shown).map((it) => (
                            <Chip key={it.id} it={it} spheres={spheres} onClick={() => openItem(it, d)} />
                          ))}
                          {extra > 0 && (
                            <button
                              type="button"
                              className="cal__more"
                              onClick={(e) => {
                                e.stopPropagation();
                                pickDay(d);
                              }}
                            >
                              +{extra} more
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ) : (
            <TimeGrid
              days={days}
              today={today}
              selected={selected}
              byDay={byDay}
              spheres={spheres}
              onDay={pickDay}
              onItem={openItem}
              height={fillH}
            />
          )}
          {items === null && !error && <p className="cal__loading">Loading…</p>}
        </div>

        {panelDay && (
          <aside className="cal__panel" aria-label={`Day ${panelDay}`}>
            <div className="cal__panel-head">
              <h3 className="cal__panel-title">
                {parseDay(panelDay).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
              </h3>
              {selected && (
                <button type="button" className="cal__navbtn" aria-label="Close day" onClick={() => { setSelected(null); setAdding(null); }}>
                  <Icon name="x" size={18} />
                </button>
              )}
            </div>
            {(byDay.get(panelDay) ?? []).length === 0 ? (
              <p className="cal__empty">Nothing on this day.</p>
            ) : (
              <ul className="cal__list">
                {(byDay.get(panelDay) ?? []).map((it) => (
                  <li key={it.id}>
                    <button type="button" className="cal__row" onClick={() => openItem(it, panelDay)}>
                      <span className="cal__dot" style={{ background: sphereColor(spheres, it.sphere) }} aria-hidden="true" />
                      <span className="cal__row-time">
                        {it.allDay || !it.startsAt ? (KIND_LABEL[it.kind] ?? 'All day') : timeLabel(it.startsAt)}
                      </span>
                      <span className="cal__row-title">{it.title}</span>
                      <span className="cal__row-job">{sphereName(spheres, it.sphere)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {adding === panelDay ? (
              <EventForm
                day={panelDay}
                spheres={spheres}
                projects={projects}
                submitLabel="Add event"
                onCancel={() => setAdding(null)}
                onSubmit={async (v) => {
                  try {
                    await createEvent({
                      title: v.title,
                      startsAt: v.startsAt,
                      endsAt: v.endsAt,
                      allDay: v.allDay,
                      kind: 'event',
                      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
                      domain: v.sphereKey ?? 'personal',
                      ...(v.sphereKey || v.projectId ? { meta: { sphereKey: v.sphereKey, projectId: v.projectId } } : {}),
                    });
                    setAdding(null);
                    toast.info(`Added ${v.title}`);
                    void load();
                  } catch (err) {
                    toast.error(`Couldn't add that: ${errorText(err)}`);
                    throw err;
                  }
                }}
              />
            ) : (
              <Button variant="secondary" icon="plus" block onClick={() => setAdding(panelDay)}>
                Add event
              </Button>
            )}
          </aside>
        )}
      </div>

      {open && (
        <EventSheet
          occ={open}
          spheres={spheres}
          projects={projects}
          onClose={() => setOpen(null)}
          onChanged={() => {
            setOpen(null);
            void load();
          }}
        />
      )}
    </Page>
  );
}

function Chip({ it, spheres, onClick }: { it: CalItem; spheres: Sphere[]; onClick: () => void }): JSX.Element {
  const time = !it.allDay && it.startsAt ? timeLabel(it.startsAt) : null;
  return (
    <button
      type="button"
      className={`cal__chip cal__chip--${it.kind}${it.allDay ? ' cal__chip--allday' : ''}`}
      style={{ '--cal-c': sphereColor(spheres, it.sphere) } as CSSProperties}
      title={`${time ? `${time} ` : ''}${it.title} · ${sphereName(spheres, it.sphere)}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {time && <span className="cal__chip-time">{time}</span>}
      <span className="cal__chip-title">{it.title}</span>
    </button>
  );
}

function TimeGrid({
  days,
  today,
  selected,
  byDay,
  spheres,
  onDay,
  onItem,
  height,
}: {
  days: string[];
  today: string;
  selected: string | null;
  byDay: Map<string, CalItem[]>;
  spheres: Sphere[];
  onDay: (d: string) => void;
  onItem: (it: CalItem, day: string) => void;
  /** Room to fill (desktop): hours stretch to it; null = the fixed 48px an hour. */
  height: number | null;
}): JSX.Element {
  // Head (~58px) + all-day row (~34px) + borders sit above the hours.
  const hourPx = height ? Math.max(40, Math.floor((height - 96) / (HOUR_TO - HOUR_FROM))) : HOUR_PX_DEFAULT;
  const hours = Array.from({ length: HOUR_TO - HOUR_FROM }, (_, i) => HOUR_FROM + i);
  const lo = HOUR_FROM * 60;
  const hi = HOUR_TO * 60;
  return (
    <div className="cal__tg" style={{ '--cal-cols': days.length } as CSSProperties}>
      <div className="cal__tg-head">
        <span className="cal__tg-gutter" />
        {days.map((d) => (
          <button
            key={d}
            type="button"
            className={`cal__tg-day${d === today ? ' cal__tg-day--today' : ''}${d === selected ? ' cal__tg-day--on' : ''}`}
            onClick={() => onDay(d)}
          >
            <span className="cal__tg-dow">{parseDay(d).toLocaleDateString(undefined, { weekday: 'short' })}</span>
            <span className="cal__tg-num">{parseDay(d).getDate()}</span>
          </button>
        ))}
      </div>
      <div className="cal__tg-allday">
        <span className="cal__tg-gutter">all-day</span>
        {days.map((d) => (
          <div key={d} className="cal__tg-allcell" onClick={() => onDay(d)}>
            {(byDay.get(d) ?? [])
              .filter((it) => it.allDay || !it.startsAt)
              .map((it) => (
                <Chip key={it.id} it={it} spheres={spheres} onClick={() => onItem(it, d)} />
              ))}
          </div>
        ))}
      </div>
      <div className="cal__tg-body" style={{ height: (HOUR_TO - HOUR_FROM) * hourPx }}>
        <div className="cal__tg-gutter cal__tg-hours">
          {hours.map((h) => (
            <span key={h} className="cal__tg-hour" style={{ top: (h - HOUR_FROM) * hourPx }}>
              {new Date(2000, 0, 1, h).toLocaleTimeString(undefined, { hour: 'numeric' })}
            </span>
          ))}
        </div>
        {days.map((d) => {
          const blocks = layLanes(
            (byDay.get(d) ?? [])
              .filter((it) => !it.allDay && it.startsAt)
              .map((it) => {
                const s = new Date(it.startsAt!);
                const e = it.endsAt ? new Date(it.endsAt) : new Date(s.getTime() + 30 * 60_000);
                const start = dayKey(s) < d ? 0 : minutesOf(s);
                const end = dayKey(e) > d ? 24 * 60 : Math.max(minutesOf(e), start + 15);
                return { it, start, end };
              }),
          );
          return (
            <div key={d} className={`cal__tg-col${d === today ? ' cal__tg-col--today' : ''}`} onClick={() => onDay(d)}>
              {hours.map((h) => (
                <span key={h} className="cal__tg-line" style={{ top: (h - HOUR_FROM) * hourPx }} />
              ))}
              {blocks.map(({ it, start, end, lane, lanes }) => {
                const top = ((Math.min(Math.max(start, lo), hi - 15) - lo) / 60) * hourPx;
                const height = Math.max(18, ((Math.min(end, hi) - Math.max(start, lo)) / 60) * hourPx);
                return (
                  <button
                    key={it.id}
                    type="button"
                    className="cal__block"
                    style={
                      {
                        top,
                        height,
                        left: `calc(${(lane / lanes) * 100}% + 2px)`,
                        width: `calc(${100 / lanes}% - 4px)`,
                        '--cal-c': sphereColor(spheres, it.sphere),
                      } as CSSProperties
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      onItem(it, d);
                    }}
                  >
                    <span className="cal__block-time">{timeLabel(it.startsAt!)}</span>
                    <span className="cal__block-title">{it.title}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface EventValues {
  title: string;
  allDay: boolean;
  startsAt: string;
  endsAt: string | null;
  sphereKey: string | null;
  projectId: string | null;
}

/** Add / edit an event: title, day, all-day or times, job, project (filtered to the job). */
function EventForm({
  day,
  initial,
  spheres,
  projects,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  day: string;
  initial?: { title: string; allDay: boolean; start: string; end: string; sphereKey: string | null; projectId: string | null };
  spheres: Sphere[];
  projects: Project[];
  submitLabel: string;
  onSubmit: (v: EventValues) => Promise<void>;
  onCancel: () => void;
}): JSX.Element {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [date, setDate] = useState(day);
  const [allDay, setAllDay] = useState(initial?.allDay ?? false);
  const [start, setStart] = useState(initial?.start ?? '09:00');
  const [end, setEnd] = useState(initial?.end ?? '10:00');
  const [sphere, setSphere] = useState(initial?.sphereKey ?? '');
  const [project, setProject] = useState(initial?.projectId ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const choices = projects.filter((p) => !sphere || p.sphereKey === sphere);

  async function save(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!title.trim() || busy) return;
    if (!allDay && end <= start) {
      setErr('The end has to be after the start.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const pj = projects.find((p) => p.id === project);
      await onSubmit({
        title: title.trim(),
        allDay,
        startsAt: localIso(date, allDay ? '12:00' : start),
        endsAt: allDay ? null : localIso(date, end),
        sphereKey: pj?.sphereKey ?? (sphere || null),
        projectId: project || null,
      });
    } catch (e2) {
      setErr(errorText(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="cal__form" onSubmit={(e) => void save(e)} aria-label={submitLabel}>
      <label className="settings-field settings-field--wide">
        Title
        <input className="settings-input" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus maxLength={200} />
      </label>
      <label className="settings-field">
        Day
        <input className="settings-input" type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
      </label>
      <label className="cal__check">
        <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} /> All day
      </label>
      {!allDay && (
        <>
          <label className="settings-field">
            Starts
            <input className="settings-input" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
          </label>
          <label className="settings-field">
            Ends
            <input className="settings-input" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
          </label>
        </>
      )}
      <label className="settings-field">
        Job
        <select
          className="settings-input"
          value={sphere}
          onChange={(e) => {
            setSphere(e.target.value);
            setProject('');
          }}
        >
          <option value="">Personal</option>
          {spheres
            .filter((s) => s.key !== 'personal')
            .map((s) => (
              <option key={s.key} value={s.key}>
                {s.name}
              </option>
            ))}
        </select>
      </label>
      <label className="settings-field">
        Project
        <select className="settings-input" value={project} onChange={(e) => setProject(e.target.value)}>
          <option value="">None</option>
          {choices.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      {err && <p className="lo-form__error settings-field--wide">{err}</p>}
      <div className="cal__form-actions">
        <Button variant="plain" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={busy || !title.trim()}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}

/** One event: what / when / job / project, with Edit and Delete. A synced row's 409 is shown as-is. */
function EventSheet({
  occ,
  spheres,
  projects,
  onClose,
  onChanged,
}: {
  occ: EventOccurrence;
  spheres: Sphere[];
  projects: Project[];
  onClose: () => void;
  onChanged: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const recurring = occ.recurrence !== undefined && occ.recurrence !== 'none';
  const project = projects.find((p) => p.id === occ.projectId) ?? null;
  const day = dayKey(new Date(occ.startsAt));
  const when = occ.allDay
    ? parseDay(day).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
    : `${new Date(occ.startsAt).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })} · ${timeLabel(occ.startsAt)}${occ.endsAt ? ` – ${timeLabel(occ.endsAt)}` : ''}`;

  async function remove(): Promise<void> {
    setErr(null);
    try {
      await deleteEvent(occ.eventId);
      onChanged();
    } catch (e) {
      setConfirm(false);
      setErr(errorText(e));
    }
  }

  async function save(v: EventValues): Promise<void> {
    const patch: EventPatch = {};
    if (v.title !== occ.title) patch.title = v.title;
    if (v.allDay !== occ.allDay) patch.allDay = v.allDay;
    // Only re-file when the job / project actually changed: a synced row keeps its connector's job otherwise.
    const wasSphere = occ.sphere && occ.sphere !== 'personal' ? occ.sphere : null;
    if (v.sphereKey !== wasSphere) patch.sphereKey = v.sphereKey;
    if (v.projectId !== (occ.projectId ?? null)) patch.projectId = v.projectId;
    const startChanged = v.startsAt !== occ.startsAt || v.allDay !== occ.allDay;
    const endChanged = (v.endsAt ?? null) !== (occ.endsAt ?? null);
    if (startChanged || endChanged) {
      if (recurring) {
        // Shift the whole series by how far this occurrence moved.
        const series = await getEvent(occ.eventId);
        const delta = Date.parse(v.startsAt) - Date.parse(occ.startsAt);
        const startsAt = new Date(Date.parse(series.startsAt) + delta);
        patch.startsAt = startsAt.toISOString();
        patch.endsAt = v.endsAt ? new Date(startsAt.getTime() + (Date.parse(v.endsAt) - Date.parse(v.startsAt))).toISOString() : null;
      } else {
        patch.startsAt = v.startsAt;
        patch.endsAt = v.endsAt;
      }
    }
    if (Object.keys(patch).length) await updateEvent(occ.eventId, patch);
    onChanged();
  }

  return (
    <Sheet title={editing ? 'Edit event' : occ.title} onClose={onClose} className="cal__sheet">
      {editing ? (
        <EventForm
          day={day}
          initial={{
            title: occ.title,
            allDay: occ.allDay,
            start: hhmm(occ.startsAt),
            end: occ.endsAt ? hhmm(occ.endsAt) : hhmm(new Date(Date.parse(occ.startsAt) + 3_600_000).toISOString()),
            sphereKey: occ.sphere && occ.sphere !== 'personal' ? occ.sphere : null,
            projectId: occ.projectId ?? null,
          }}
          spheres={spheres}
          projects={projects}
          submitLabel="Save"
          onCancel={() => setEditing(false)}
          onSubmit={save}
        />
      ) : (
        <div className="cal__detail">
          <p className="cal__detail-when">
            <Icon name="clock" size={16} /> {when}
          </p>
          {recurring && (
            <p className="cal__detail-meta">
              <Icon name="repeat" size={16} /> Repeats {occ.recurrence} — edits and delete apply to the whole series
            </p>
          )}
          <p className="cal__detail-meta">
            <span className="cal__dot" style={{ background: sphereColor(spheres, occ.sphere) }} aria-hidden="true" />
            {sphereName(spheres, occ.sphere)}
            {project && (
              <>
                {' · '}
                <Link to={`/projects/${project.id}`}>{project.name}</Link>
              </>
            )}
          </p>
          {occ.location && <p className="cal__detail-meta">{occ.location}</p>}
          {occ.detail && <p className="cal__detail-text">{occ.detail}</p>}
          {occ.onRemote && <p className="cal__detail-hint">On your synced calendar.</p>}
          {err && (
            <p className="cal__error" role="alert">
              {err}
            </p>
          )}
          <div className="cal__form-actions">
            {confirm ? (
              <>
                <Button variant="plain" onClick={() => setConfirm(false)}>
                  Keep
                </Button>
                <Button variant="destructive" onClick={() => void remove()}>
                  {recurring ? 'Delete series' : 'Delete event'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" icon="trash" onClick={() => setConfirm(true)}>
                  Delete
                </Button>
                <Button icon="pencil" onClick={() => setEditing(true)}>
                  Edit
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </Sheet>
  );
}
